import { ITimerSubscriber, ITimerSubscribeOptions } from './interfaces';
import {
    createFormatsList,
    getEndTime,
    findNextFormat,
    calculatePeriods,
    generateTimeString,
    durations,
    dateNow,
} from './utils';

export class Timer {
    private readonly offsetSeconds: number = 0;

    private readonly subscriptions: Set<ITimerSubscriber> = new Set();

    private endTime: number;

    private currentMsLeftWithOffset: number;

    public currentSecondsLeft: number;

    public finished = false;

    public finishedWithOffset = false;

    constructor(seconds: number, offsetSeconds = 0) {
        this.endTime = getEndTime(Math.max(seconds, 0));
        this.offsetSeconds = offsetSeconds;

        this.tick(dateNow());
    }

    private setCurrentTime(currentTime: number): void {
        this.currentMsLeftWithOffset = Math.max(this.endTime - currentTime, 0);
        this.currentSecondsLeft = Math.max(
            this.currentMsLeftWithOffset / 1000 - this.offsetSeconds,
            0,
        );
    }

    public tick(currentTime: number): void {
        if (this.finishedWithOffset) {
            return;
        }

        this.setCurrentTime(currentTime);

        if (!this.currentMsLeftWithOffset) {
            this.finishedWithOffset = true;
        }

        if (!this.currentSecondsLeft) {
            this.finished = true;
        }

        this.subscriptions.forEach((subscriber) => {
            if (!this.finished) {
                if (subscriber.onTick) {
                    const currentFormat = findNextFormat(
                        subscriber.formatsList,
                        this.currentSecondsLeft,
                    );
                    const periods = calculatePeriods(
                        this.endTime - this.offsetSeconds * 1000,
                        currentTime,
                        currentFormat.requiredDurationValues,
                    );
                    const timeLeftString = generateTimeString(
                        currentFormat.requiredDurationValues,
                        periods,
                        durations.units,
                        subscriber.showDurationUnits,
                    );

                    subscriber.onTick(timeLeftString, this.currentSecondsLeft);
                }

                if (subscriber.onAfterExactSecond) {
                    if (
                        this.currentSecondsLeft <= subscriber.onAfterExactSecond.second &&
                        !subscriber.onAfterExactSecond.callback.called
                    ) {
                        subscriber.onAfterExactSecond.callback();
                        subscriber.onAfterExactSecond.callback.called = true;
                    }
                }
            } else {
                subscriber.onFinish();
            }

            if (this.finishedWithOffset) {
                subscriber.onFinishWithOffset();
            }
        });
    }

    public update(seconds: number): void {
        const secondsLeft = Math.max(seconds, 0);

        if (secondsLeft) {
            this.endTime = getEndTime(secondsLeft);
            this.finished = false;
            this.finishedWithOffset = false;

            const currentTime = dateNow();

            this.setCurrentTime(currentTime);

            this.subscriptions.forEach((subscriber) => {
                if (subscriber.onAfterExactSecond) {
                    subscriber.onAfterExactSecond.callback.called = false;
                }

                subscriber.onUpdate();
            });

            this.tick(currentTime);
        }
    }

    public subscribe({
        onFinish = () => {},
        onFinishWithOffset = () => {},
        onUpdate = () => {},
        format = 'dhMS',
        showDurationUnits = false,
        onAfterExactSecond,
        ...otherOptions
    }: ITimerSubscribeOptions): () => void {
        const subscriber = {
            onFinish,
            onFinishWithOffset,
            onUpdate,
            onAfterExactSecond,
            showDurationUnits,
            formatsList: createFormatsList(format),
            ...otherOptions,
        };

        this.subscriptions.add(subscriber);
        this.tick(dateNow());

        return () => {
            this.subscriptions.delete(subscriber);
        };
    }

    public stop(): void {
        if (this.finished && this.finishedWithOffset) {
            return;
        }

        this.finished = true;
        this.finishedWithOffset = true;
        this.currentMsLeftWithOffset = 0;
        this.currentSecondsLeft = 0;

        this.subscriptions.forEach((subscriber) => {
            subscriber.onFinish();
            subscriber.onFinishWithOffset();
        });
    }
}
